import { useState } from "react"
import { ChevronDown, ChevronRight, Wrench, Check, X } from "lucide-react"
import { cn } from "@/lib/cn"

type Props = {
  /** Tool call (model invoking it) — input shape varies by tool. */
  toolCall?:   { id?: string; name?: string; input?: unknown }
  /** Tool result paired with the call. */
  toolResult?: { id?: string; name?: string; output?: unknown; is_error?: boolean }
  timestamp?:  string | null
}

// Single chip per tool invocation. When BOTH a call and its matching
// result are present we merge them into one expandable box (one row in
// the conversation, two payloads inside). Green when the tool succeeded
// or is still in flight; red on error. The previous gold-vs-green split
// was confusing — every tool use is a single conceptual event from the
// operator's perspective.
export function ToolCallChip({ toolCall, toolResult, timestamp }: Props) {
  const [open, setOpen] = useState(false)
  const name      = toolCall?.name ?? toolResult?.name ?? "tool"
  const isError   = toolResult?.is_error === true
  const hasResult = !!toolResult
  const tone = isError
    ? "border-status-error/30 bg-status-error/10 text-status-error"
    : "border-status-success/30 bg-status-success/10 text-status-success"
  const Icon = isError ? X : (hasResult ? Check : Wrench)

  return (
    <div className={cn("rounded-control border px-2 py-1 text-xs", tone)}>
      <button
        type="button"
        className="flex w-full items-center gap-1.5"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={11} strokeWidth={2} /> : <ChevronRight size={11} strokeWidth={2} />}
        <Icon size={11} strokeWidth={2} />
        <span className="truncate">
          <code>{name}</code>
          {!hasResult && <span className="text-app-subtle"> · running</span>}
          {isError && <span className="text-app-subtle"> · errored</span>}
        </span>
        {timestamp && (
          <span className="ml-auto text-[10px] text-app-subtle">
            {new Date(timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-1.5 space-y-2">
          {toolCall && (
            <Panel label="Input">
              {JSON.stringify(toolCall.input ?? {}, null, 2)}
            </Panel>
          )}
          {hasResult && (
            <Panel label={isError ? "Error" : "Output"}>
              {JSON.stringify(toolResult.output ?? {}, null, 2)}
            </Panel>
          )}
        </div>
      )}
    </div>
  )
}

function Panel({ label, children }: { label: string; children: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-subtle">{label}</div>
      <pre className="max-h-48 overflow-auto rounded bg-app-bg p-2 text-[10px] text-app-muted">{children}</pre>
    </div>
  )
}
